/**
 * `/inbox` — the operator-mail board (operator-inbox slice 5, 2026-08-01).
 *
 * Mail to the studio's scanned mailboxes — Mario's own
 * (`m.kaspers@e-pulsive.co.uk`, with the forwarded personal addresses landing
 * in it) and, since 2026-08-06/07, the BUSINESS pair `contact@` + `hello@`,
 * whose member mail files notify-only per `gmail.mailboxes[].member_mail` —
 * triaged by the `inbox_triage_scanner` into MATTERS
 * (`inbox_threads`, migration 145) the way `/pr` renders Peter's
 * collaborations. Three sections:
 *
 *   Overview   — open / awaiting-you / needs-you / to-notify / replied / closed,
 *                studio-wide, filter-independent.
 *   Pipeline   — matters grouped by stage in board (enum) order, one TABLE per
 *                stage, each row carrying its classification + first-arrival
 *                stamp strip. It was a card grid until 2026-08-06; the cards
 *                spent about ten lines on the eight facts a row states in two,
 *                and this board is read by scanning many matters at once, not
 *                by dwelling on one. The TAB STRIP is the one thing `/pr` has
 *                no analogue for: it filters the pipeline to one receiving
 *                address (client-side; the tabs are `by_mailbox` — mailboxes
 *                with OPEN matters — unioned with `declared_mailboxes`, the
 *                ones the studio says it scans, so a quiet mailbox still shows
 *                a `(0)` tab rather than looking broken) and,
 *                since 2026-08-06, also switches the page to a CHANNEL —
 *                WhatsApp or Instagram — which renders a read-only conversation
 *                list instead of the matter pipeline. Mail is a stage machine
 *                with an approval loop; chats are a view. One strip, two
 *                bodies, because "where did this reach us" is the same question
 *                either way. The chat tabs kept their list-and-thread layout:
 *                the cards complaint was about the mail side.
 *   People     — every correspondent with the directional last-contact /
 *                last-reply split, linking to the matter detail.
 *
 * Read-only. Every mutation goes through the six `*_inbox_*` canonical
 * actions the scanner + agent drive, or the thread detail's affordances. One
 * aggregate fetch, `GET /api/inbox-board`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type ChatsBoardResponse,
  type InboxBoardCorrespondent,
  type InboxBoardResponse,
  type InboxThread,
  type InstagramCommentsResponse,
} from "../lib/api";
import { ActionModalButton } from "../components/ActionModalButton";
import { ChatList } from "../components/ChatList";
import { InstagramCommentList } from "../components/InstagramCommentList";
import { PendingApprovalsPanel } from "../components/PendingApprovalsPanel";
import {
  INBOX_BUCKET_LABELS,
  bucketInboxGroups,
  inboxBucketForStage,
  urgencyTone,
} from "../lib/inbox-board";
import { pipelineOutcome } from "../lib/pipeline-outcome";
import { studioFormat } from "../lib/studio-tz";

const DATE_OPTS: Intl.DateTimeFormatOptions = { month: "short", day: "2-digit" };
const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmt(iso: string | null, opts: Intl.DateTimeFormatOptions): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(opts).format(d);
}

const fmtDate = (iso: string | null): string => fmt(iso, DATE_OPTS);
const fmtDateTime = (iso: string | null): string => fmt(iso, DATETIME_OPTS);

/**
 * Namespace for the channel tabs. A mailbox tab's key IS the email address, so
 * a channel needs a prefix that no address can collide with — `chat:` works
 * because a bare `whatsapp` would be a legal (if odd) local part, while
 * `chat:whatsapp` is not a valid address at all.
 */
const CHAT_TAB_PREFIX = "chat:";

/**
 * The pseudo-channel for Instagram COMMENTS (2026-08-07). It rides the same
 * `chat:` tab machinery as the real conversation channels, but it is not a
 * conversation: comments are public, have no sender thread, and are answered
 * with `reply_instagram_comment` rather than a DM send. Kept distinct from
 * `instagram` (the DM tab) so the two never merge into one ambiguous list.
 */
const INSTAGRAM_COMMENTS_CHANNEL = "instagram_comments";

function channelTabLabel(channel: string): string {
  return channel === "whatsapp"
    ? "WhatsApp"
    : channel === "instagram"
      ? "Instagram"
      : channel === INSTAGRAM_COMMENTS_CHANNEL
        ? "Instagram comments"
        : channel;
}

export function Inbox(): JSX.Element {
  const [data, setData] = useState<InboxBoardResponse | null>(null);
  const [chats, setChats] = useState<ChatsBoardResponse | null>(null);
  const [igComments, setIgComments] =
    useState<InstagramCommentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  /**
   * The selected tab. `null` = All (mail pipeline, every mailbox); a mailbox
   * address filters the pipeline; `chat:whatsapp` / `chat:instagram` swaps the
   * body for that channel's conversation list.
   */
  const [tab, setTab] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState<boolean>(false);
  const navigate = useNavigate();

  // The chats board is fetched alongside the mail board rather than on tab
  // click: the tab strip shows per-channel counts, so the numbers must exist
  // before anyone opens the tab. A chats failure must NOT blank the mail
  // pipeline, so it is caught separately and degrades to no channel tabs.
  useEffect(() => {
    let cancelled = false;
    api
      .inboxChats()
      .then((r) => {
        if (!cancelled) setChats(r);
      })
      .catch(() => {
        if (!cancelled) setChats(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Instagram comments (2026-08-07). Fetched alongside the others for the same
  // reason: the tab strip carries the count, so it must exist before the tab
  // is opened. Degrades to no tab on failure — a comment surface that errors
  // must not blank the operator's mail pipeline.
  const refreshIgComments = useCallback(() => {
    api
      .instagramComments()
      .then((r) => setIgComments(r))
      .catch(() => setIgComments(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .instagramComments()
      .then((r) => {
        if (!cancelled) setIgComments(r);
      })
      .catch(() => {
        if (!cancelled) setIgComments(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Lifted out of the mount effect so a row control can refetch after it
   * mutates. `useDispatch`'s automatic `invalidateAfterMutation()` does NOT
   * reach this board — it busts the `resource-cache` keys, and this page
   * fetches with a plain effect — so without an explicit refetch every button
   * would appear to do nothing.
   */
  const loadBoard = useCallback((): void => {
    // Deliberately does NOT set `loading` — a REFETCH must not blank the page.
    // The component early-returns a loading paragraph while `loading` is true,
    // which unmounts everything including any open action modal. The first load
    // still shows the spinner because `loading` starts true and is cleared here.
    api
      .inboxBoard()
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  /**
   * The mailbox tabs — every mailbox that HAS open matters, unioned with every
   * mailbox the studio DECLARES as scanned (2026-08-07).
   *
   * `by_mailbox` alone was not the tab strip it looked like: it counts OPEN
   * threads (`AND stage <> 'closed'`), so a scanned mailbox that had filed
   * nothing had no tab at all — indistinguishable from a mailbox whose scanner
   * had quietly stopped — and a mailbox whose last matter closed VANISHED from
   * the strip mid-session. `contact@` sat in exactly that state: scanned, 14
   * messages read, no tab.
   *
   * Both keys are lowercase (`by_mailbox` is keyed on
   * `lower(mailbox_identifier)`; the server lowercases the declared list), so
   * the Set union cannot mint a second tab for one mailbox.
   */
  const mailboxes = useMemo(() => {
    if (data === null) return [];
    return [
      ...new Set([
        ...Object.keys(data.counts.by_mailbox),
        ...(data.declared_mailboxes ?? []),
      ]),
    ].sort();
  }, [data]);

  const chatChannels = useMemo(() => {
    const real =
      chats === null
        ? []
        : Object.keys(chats.counts.by_channel)
            .filter((c) => (chats.counts.by_channel[c] ?? 0) > 0)
            .sort();
    // The comments pseudo-channel is appended AFTER the sort, not folded into
    // it: it is a different kind of thing from a conversation channel, and
    // sorting it alphabetically would drop "Instagram comments" between
    // "Instagram" and "WhatsApp" as if it were a third inbox of DMs.
    return (igComments?.counts.total ?? 0) > 0
      ? [...real, INSTAGRAM_COMMENTS_CHANNEL]
      : real;
  }, [chats, igComments]);

  /** The channel this tab selects, or null when the tab is a mailbox / All. */
  const activeChannel =
    tab !== null && tab.startsWith(CHAT_TAB_PREFIX)
      ? tab.slice(CHAT_TAB_PREFIX.length)
      : null;
  const mailbox = activeChannel === null ? tab : null;

  // 2026-08-15 (inbox-pr-simplify) — the board renders three BUCKETS
  // (Needs you / Being handled / Closed) instead of nine stage sections;
  // the stage machine itself is untouched (it drives the approval loop).
  const visibleGroups = useMemo(() => {
    if (data === null) return [];
    const filtered = data.groups
      .filter((g) => showClosed || g.stage !== "closed")
      .map((g) => ({
        stage: g.stage,
        threads:
          mailbox === null
            ? g.threads
            : g.threads.filter(
                (t) => t.mailbox_identifier.toLowerCase() === mailbox,
              ),
      }))
      .filter((g) => g.threads.length > 0);
    return bucketInboxGroups(filtered);
  }, [data, mailbox, showClosed]);

  if (loading) return <p className="empty">Loading inbox…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load the inbox ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const c = data.counts;

  return (
    <div>
      <h1 className="page-title">Inbox</h1>

      {/* `Replied` is counted from TIMESTAMPS, not the current stage, so a
          matter that replied and moved on still counts and the figure never
          goes down. Said on the page once; kept here because it is a property
          of the number, and this is where the next reader will look. */}
      <div className="pr-overview">
        <OverviewStat label="Open" value={c.open} />
        <OverviewStat label="Awaiting you" value={c.awaiting_mario} />
        <OverviewStat label="Needs you" value={c.needs_mario} />
        <OverviewStat label="To notify" value={c.notify_pending} />
        <OverviewStat label="Replied" value={c.replied} />
        <OverviewStat label="Closed" value={c.closed} />
      </div>

      {/* Approvals FIRST — a staged reply is the only thing on this page
          waiting on a human. Shown on the email pipeline only: the WhatsApp and
          Instagram tabs are deliberately read-only, and this queue is email
          drafts regardless of which tab happens to be selected. */}
      {activeChannel === null ? (
        <PendingApprovalsPanel
          lane="inbox"
          drain={data.drain ?? null}
          onResolved={loadBoard}
        />
      ) : null}

      <div className="pr-section-head">
        <h2 className="pr-heading">
          {activeChannel === null
            ? "Pipeline"
            : activeChannel === INSTAGRAM_COMMENTS_CHANNEL
              ? // NOT "… conversations". A public comment under the studio's
                // own post is not a thread with a member — there is nobody
                // resolved on the other end and nothing to continue.
                channelTabLabel(activeChannel)
              : `${channelTabLabel(activeChannel)} conversations`}
        </h2>
        {activeChannel === null && c.closed > 0 ? (
          <button
            type="button"
            className="pr-toggle"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? "Hide" : "Show"} closed ({c.closed})
          </button>
        ) : null}
        {/* The chat tabs mirror conversations owned by the member / PR pages,
            so they say "reply elsewhere". The comments tab is the OPPOSITE:
            it is the only surface that can answer a comment, and it carries a
            composer per row — so the read-only notice must NOT show here, or
            the page contradicts the button directly beneath it. */}
        {activeChannel !== null &&
        activeChannel !== INSTAGRAM_COMMENTS_CHANNEL ? (
          <span className="subtle">
            Read-only — reply from the member or PR page
          </span>
        ) : null}
      </div>

      {/* The tab strip — "where did this reach us". Mail accounts filter the
          pipeline; channel tabs replace it with a read-only conversation list.
          Renders once there is more than one thing to choose between.

          The threshold is unchanged, but what feeds it is not: `mailboxes` now
          includes DECLARED scanned mailboxes, so a studio that declares two and
          has received nothing gets the strip where before it got none. That is
          the intended reading — "these are the two addresses we read, both
          quiet" — and it is a deliberate consequence, not a side effect. A
          studio with ONE declared mailbox and no chats still shows nothing:
          there is still only one thing to choose between.

          The comments pseudo-channel then forces it open on its own, as a
          DISJUNCT that leaves the count above untouched. The `> 1` heuristic
          assumes every tab is a SUBSET of the mail pipeline, so one mailbox and
          "All mail" really are the same list and the strip is noise. Instagram
          comments are not mail at all — a studio with no mail threads yet (a
          fresh tenant, or one that only runs Instagram) would otherwise have
          its comments ingested, alerted and completely unreachable, which is
          the silent-invisibility failure this whole surface exists to prevent.
          Found while rendering the tab against a mail-less database,
          2026-08-07. */}
      {mailboxes.length + chatChannels.length > 1 ||
      chatChannels.includes(INSTAGRAM_COMMENTS_CHANNEL) ? (
        <div className="pr-type-tabs" role="tablist" aria-label="Account or channel">
          <button
            type="button"
            role="tab"
            aria-selected={tab === null}
            className={`pr-type-tab${tab === null ? " active" : ""}`}
            onClick={() => setTab(null)}
          >
            All mail
          </button>
          {mailboxes.map((m) => {
            // `?? 0` is load-bearing, not defensive: the web tsconfig sets
            // `noUncheckedIndexedAccess`, so this lookup is `number |
            // undefined`, and a declared-but-empty mailbox would render
            // `contact@e-pulsive.co.uk ()` with NO type error. The chat tabs
            // below already do the same.
            const open = c.by_mailbox[m] ?? 0;
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={tab === m}
                className={`pr-type-tab${tab === m ? " active" : ""}`}
                onClick={() => setTab(m)}
                title={
                  open > 0
                    ? `${open} open ${open === 1 ? "matter" : "matters"}`
                    : "Scanned — no open matters"
                }
              >
                {m} ({open})
              </button>
            );
          })}
          {chatChannels.map((ch) => {
            const key = `${CHAT_TAB_PREFIX}${ch}`;
            const isComments = ch === INSTAGRAM_COMMENTS_CHANNEL;
            // The comments tab counts come from its OWN payload — the chats
            // board knows nothing about `instagram_engagement_events`.
            const waiting = isComments
              ? (igComments?.counts.awaiting_reply ?? 0)
              : (chats?.counts.awaiting_by_channel[ch] ?? 0);
            const total = isComments
              ? (igComments?.counts.total ?? 0)
              : (chats?.counts.by_channel[ch] ?? 0);
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`pr-type-tab${tab === key ? " active" : ""}`}
                onClick={() => setTab(key)}
                title={
                  waiting > 0
                    ? `${waiting} awaiting a reply`
                    : "Nobody is waiting on a reply"
                }
              >
                {channelTabLabel(ch)} ({total}
                {waiting > 0 ? ` · ${waiting} waiting` : ""})
              </button>
            );
          })}
        </div>
      ) : null}

      {activeChannel === INSTAGRAM_COMMENTS_CHANNEL ? (
        <InstagramCommentList
          board={igComments}
          onReplied={refreshIgComments}
        />
      ) : activeChannel !== null ? (
        <ChatList
          channel={activeChannel}
          conversations={(chats?.conversations ?? []).filter(
            (v) => v.channel === activeChannel,
          )}
        />
      ) : visibleGroups.length === 0 ? (
        <p className="empty">
          {c.total === 0
            ? "No matters yet — the scanner files one when relevant mail arrives."
            : "Nothing live. Every matter is closed."}
        </p>
      ) : (
        visibleGroups.map((g) => (
          <section key={g.bucket} className="pr-stage">
            <h3 className="pr-stage-title">
              {INBOX_BUCKET_LABELS[g.bucket]}
              <span className="dc-muted"> · {g.threads.length}</span>
            </h3>
            <div className="table-scroll-x">
              <table className="pr-people-table">
                <thead>
                  <tr>
                    <th>Matter</th>
                    <th>Category</th>
                    <th>Urgency</th>
                    <th>Mailbox</th>
                    <th>Summary</th>
                    <th>Next</th>
                    <th>Last event</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.threads.map((t) => (
                    <MatterRow
                      key={t.inbox_thread_id}
                      thread={t}
                      navigate={navigate}
                      onChanged={loadBoard}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
      {activeChannel === null && data.has_more ? (
        <p className="subtle">
          Showing the first{" "}
          {data.groups.reduce((n, g) => n + g.threads.length, 0)} matters —
          older closed ones are not listed.
        </p>
      ) : null}

      {activeChannel !== null ? null : (
        <>
      <div className="pr-section-head">
        <h2 className="pr-heading">People</h2>
        <span className="subtle">
          {data.correspondents.length} correspondent
          {data.correspondents.length === 1 ? "" : "s"}
        </span>
      </div>

      {data.correspondents.length === 0 ? (
        <p className="empty">No correspondents yet.</p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Email</th>
                <th>Status</th>
                <th className="pr-num">Open</th>
                <th>Latest matter</th>
                <th>Last contact</th>
                <th>Last reply</th>
              </tr>
            </thead>
            <tbody>
              {data.correspondents.map((p) => (
                <CorrespondentRow key={p.id} person={p} navigate={navigate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
}

function OverviewStat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}): JSX.Element {
  return (
    <div className="pr-stat">
      <div className="pr-stat-value">{value}</div>
      <div className="pr-stat-label">{label}</div>
    </div>
  );
}

/**
 * One matter. Carries every fact the card it replaced carried — name, subject,
 * category, urgency, the notify bell, the summary, the receiving mailbox, the
 * revision round, the five stamps, the next action (or the closing reason) and
 * the last event — in two lines rather than ten.
 */
function MatterRow({
  thread: t,
  navigate,
  onChanged,
}: {
  readonly thread: InboxThread;
  readonly navigate: ReturnType<typeof useNavigate>;
  /** Refetch the board after a row control mutates the matter. */
  readonly onChanged: () => void;
}): JSX.Element {
  const outcome = pipelineOutcome(t);
  const to = `/inbox/threads/${encodeURIComponent(t.inbox_thread_id)}`;
  return (
    <tr className="pr-row" onClick={() => navigate(to)} title="Open matter">
      <td className="pr-cell-title">
        {t.target_name}
        {t.notify_pending ? (
          <span
            className="tag pr-member-flag"
            title="A notification is owed or retrying"
          >
            🔔
          </span>
        ) : null}
        {t.subject !== null ? (
          <div className="pr-sub dc-muted pr-clamp" title={t.subject}>
            {t.subject}
          </div>
        ) : null}
        {t.revision_round > 0 ? (
          <div className="pr-sub dc-muted">revision {t.revision_round}</div>
        ) : null}
      </td>
      <td>
        {t.category !== null ? (
          <span className="pr-type-badge">{t.category}</span>
        ) : (
          <span className="dc-muted">—</span>
        )}
      </td>
      <td>
        {t.urgency !== null && t.urgency !== "normal" ? (
          <span className={`tag ${urgencyTone(t.urgency)}`}>{t.urgency}</span>
        ) : (
          <span className="dc-muted">—</span>
        )}
      </td>
      <td className="pr-lastcontact">{t.mailbox_identifier}</td>
      <td className="pr-cell-prose">
        {t.summary === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <div className="pr-clamp" title={t.summary}>
            {t.summary}
          </div>
        )}
      </td>
      <td className="pr-cell-next">
        {outcome.empty ? (
          <span className="dc-muted">—</span>
        ) : (
          <>
            <div>
              {outcome.label} {fmtDate(outcome.at)}
            </div>
            {outcome.note !== null ? (
              <div className="pr-sub dc-muted pr-clamp" title={outcome.note}>
                {outcome.note}
              </div>
            ) : null}
          </>
        )}
      </td>
      <td className="pr-cell-prose">
        {t.last_event === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <>
            <div className="pr-clamp" title={t.last_event.summary}>
              {t.last_event.summary}
            </div>
            <div className="pr-sub dc-muted">
              {fmtDateTime(t.last_event.at)}
            </div>
          </>
        )}
      </td>
      {/* Row actions. The whole <tr> navigates, so this cell must stop the
          click propagating or opening the modal would also open the matter
          (the /data row-delete precedent). */}
      <td
        className="data-td-actions"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {t.stage !== "closed" ? (
          <ActionModalButton
            action="update_inbox_thread"
            buttonLabel="Close"
            buttonClassName="data-row-delete-btn"
            buttonTitle="Take this matter off the board"
            modalTitle="Close this matter"
            description={t.subject ?? t.summary ?? "Untitled matter"}
            prefill={{ inbox_thread_id: t.inbox_thread_id, stage: "closed" }}
            fields={[
              {
                kind: "textarea",
                name: "closed_reason",
                label: "Why is this closed?",
                required: true,
                maxLength: 500,
                placeholder: "resolved · dropped · lapsed · …",
              },
            ]}
            submitLabel="Close matter"
            onDone={onChanged}
          />
        ) : null}
      </td>
    </tr>
  );
}

function CorrespondentRow({
  person: p,
  navigate,
}: {
  readonly person: InboxBoardCorrespondent;
  readonly navigate: ReturnType<typeof useNavigate>;
}): JSX.Element {
  // Directional split (the PR People rule): "Last contact" is OUR last
  // outbound to them; "Last reply" is THEIR last inbound to us.
  const to =
    p.latest_thread_id !== null
      ? `/inbox/threads/${encodeURIComponent(p.latest_thread_id)}`
      : `/pr/people/${encodeURIComponent(p.id)}`;
  return (
    <tr className="pr-row" onClick={() => navigate(to)} title="Open latest matter">
      <td className="pr-name">{p.name}</td>
      <td>
        <span className="pr-type-badge">{p.type}</span>
      </td>
      <td>{p.email ?? <span className="dc-muted">—</span>}</td>
      <td>
        <span className="tag">{p.status}</span>
      </td>
      <td className="pr-num">
        {p.open_threads > 0 ? (
          p.open_threads
        ) : (
          <span className="dc-muted">0</span>
        )}
      </td>
      <td>
        {p.latest_thread_stage === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <span className="pr-stage-chip">
            {INBOX_BUCKET_LABELS[inboxBucketForStage(p.latest_thread_stage)]}
          </span>
        )}
      </td>
      <td className="pr-lastcontact">{fmtDate(p.contact_date)}</td>
      <td className="pr-lastcontact">{fmtDate(p.response_date)}</td>
    </tr>
  );
}
